// SUMMARY: Link-styled marketing controls (primary warm white, secondary dark glass, ghost text) with optional barcode mark

import Link from 'next/link'
import type { ComponentProps } from 'react'
import { BrandMark } from '@/components/brand-mark'
import { cn } from '@/lib/utils'

type Variant = 'primary' | 'secondary' | 'ghost'
type Size = 'default' | 'sm'

const VARIANT_CLASSES: Record<Variant, string> = {
	primary: 'border-transparent bg-primary text-primary-foreground hover:bg-primary/88',
	secondary:
		'edge-highlight border-border/40 bg-secondary text-foreground backdrop-blur-[8px] hover:bg-primary hover:text-primary-foreground',
	ghost: 'border-transparent bg-transparent text-body hover:bg-secondary hover:text-foreground',
}

const SIZE_CLASSES: Record<Size, string> = {
	default: 'h-10 gap-2 px-4 text-sm leading-6',
	sm: 'h-8 gap-1.5 px-3 text-xs leading-4',
}

interface MarketingButtonProps extends Omit<ComponentProps<typeof Link>, 'className'> {
	variant?: Variant
	size?: Size
	withMark?: boolean
	className?: string
}

export function MarketingButton({
	variant = 'primary',
	size = 'default',
	withMark = false,
	className,
	children,
	...props
}: MarketingButtonProps) {
	return (
		<Link
			className={cn(
				'group inline-flex items-center justify-center rounded-lg border font-medium whitespace-nowrap transition-colors duration-150 outline-none select-none focus-visible:ring-3 focus-visible:ring-ring/50',
				VARIANT_CLASSES[variant],
				SIZE_CLASSES[size],
				className,
			)}
			{...props}
		>
			{withMark && <BrandMark shimmer="hover" className={cn('w-auto', size === 'sm' ? 'h-2.5' : 'h-3.5')} />}
			{children}
		</Link>
	)
}
